import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Mail, CheckCircle, Eye, MousePointer, AlertTriangle, XCircle, RefreshCw, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = (process.env.REACT_APP_BACKEND_URL || window.location.origin);

const STATUS_CFG = {
  sent:      { label: "Envoye",   color: "bg-blue-500/20 text-blue-400",   icon: Mail },
  delivered: { label: "Delivre",  color: "bg-green-500/20 text-green-400", icon: CheckCircle },
  opened:    { label: "Ouvert",   color: "bg-cyan-500/20 text-cyan-400",   icon: Eye },
  clicked:   { label: "Clique",   color: "bg-purple-500/20 text-purple-400", icon: MousePointer },
  bounced:   { label: "Rebondi",  color: "bg-orange-500/20 text-orange-400", icon: AlertTriangle },
  complained:{ label: "Plainte",  color: "bg-red-500/20 text-red-400",     icon: XCircle },
  failed:    { label: "Echec",    color: "bg-red-500/20 text-red-400",     icon: XCircle },
};

const TYPE_LABELS = {
  proposal: "Proposition", contract: "Contrat", invoice: "Facture",
  ticket: "Ticket", contact: "Contact", reminder: "Relance",
  welcome: "Bienvenue", general: "General",
};

export default function EmailDashboardPage() {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ email_type: "", status: "" });
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.email_type) params.email_type = filters.email_type;
      if (filters.status) params.status = filters.status;
      const [logsRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/admin/emails`, { headers, params }),
        axios.get(`${API}/api/admin/emails/stats`, { headers }),
      ]);
      setLogs(logsRes.data);
      setStats(statsRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const statCards = [
    { key: "total",     label: "Total",    icon: Mail,          accent: "border-[#0099CC]" },
    { key: "sent",      label: "Envoyes",  icon: Mail,          accent: "border-blue-500" },
    { key: "delivered", label: "Delivres", icon: CheckCircle,   accent: "border-green-500" },
    { key: "opened",    label: "Ouverts",  icon: Eye,           accent: "border-cyan-500" },
    { key: "clicked",   label: "Cliques",  icon: MousePointer,  accent: "border-purple-500" },
    { key: "failed",    label: "Echecs",   icon: XCircle,       accent: "border-red-500" },
  ];

  return (
    <div className="space-y-6" data-testid="email-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Suivi des Emails</h1>
          <p className="text-[#64748b] text-sm mt-1">Tracking temps reel via Resend</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm" className="border-[#2A3441] text-[#94A3B8] hover:text-white" data-testid="refresh-emails-btn">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Actualiser
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {statCards.map(({ key, label, icon: Icon, accent }) => (
            <div key={key} className={`bg-[#151B2B] rounded-lg p-4 border-l-2 ${accent} border-t border-r border-b border-[#2A3441]`} data-testid={`email-stat-${key}`}>
              <div className="flex items-center gap-2 mb-1">
                <Icon className="h-4 w-4 text-[#64748b]" />
                <span className="text-[#64748b] text-xs">{label}</span>
              </div>
              <p className="text-white text-xl font-bold">{stats[key] || 0}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center" data-testid="email-filters">
        <Filter className="h-4 w-4 text-[#64748b]" />
        <select
          value={filters.email_type}
          onChange={e => setFilters(f => ({ ...f, email_type: e.target.value }))}
          className="bg-[#151B2B] text-white border border-[#2A3441] rounded-lg px-3 py-2 text-sm"
          data-testid="email-filter-type"
        >
          <option value="">Tous les types</option>
          {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select
          value={filters.status}
          onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
          className="bg-[#151B2B] text-white border border-[#2A3441] rounded-lg px-3 py-2 text-sm"
          data-testid="email-filter-status"
        >
          <option value="">Tous les statuts</option>
          {Object.keys(STATUS_CFG).map(k => <option key={k} value={k}>{STATUS_CFG[k].label}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-[#151B2B] rounded-xl border border-[#2A3441] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="email-logs-table">
            <thead>
              <tr className="border-b border-[#2A3441] text-[#64748b] text-xs uppercase">
                <th className="text-left px-4 py-3">Statut</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">Destinataire</th>
                <th className="text-left px-4 py-3">Sujet</th>
                <th className="text-left px-4 py-3">Resend ID</th>
                <th className="text-left px-4 py-3">Date</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-[#64748b]">Aucun email enregistre</td></tr>
              )}
              {logs.map(log => {
                const cfg = STATUS_CFG[log.status] || STATUS_CFG.sent;
                const Icon = cfg.icon;
                return (
                  <tr key={log.id} className="border-b border-[#2A3441]/50 hover:bg-[#1A2235] transition-colors" data-testid={`email-row-${log.id}`}>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${cfg.color}`}>
                        <Icon className="h-3 w-3" /> {cfg.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[#94A3B8] text-sm">{TYPE_LABELS[log.email_type] || log.email_type}</span>
                    </td>
                    <td className="px-4 py-3 text-white text-sm">{log.to}</td>
                    <td className="px-4 py-3 text-white text-sm max-w-[280px] truncate">{log.subject}</td>
                    <td className="px-4 py-3">
                      <span className="text-[#64748b] text-xs font-mono">{log.resend_id ? log.resend_id.substring(0, 12) + "..." : "—"}</span>
                    </td>
                    <td className="px-4 py-3 text-[#94A3B8] text-sm whitespace-nowrap">
                      {log.created_at && !isNaN(new Date(log.created_at).getTime())
                        ? new Date(log.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
                        : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Error details for failed emails */}
      {logs.some(l => l.status === "failed" && l.error) && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4" data-testid="email-errors-section">
          <h3 className="text-red-400 font-semibold text-sm mb-2">Erreurs recentes</h3>
          {logs.filter(l => l.status === "failed" && l.error).slice(0, 5).map(l => (
            <div key={l.id} className="text-red-300/80 text-xs mb-1 font-mono">{l.to}: {l.error}</div>
          ))}
        </div>
      )}
    </div>
  );
}
